import { createSelector } from '@/redux/lib/createSelector.js';

export const companies = (state) => state.settings.companies;
export const jobTypes = (state) => state.settings.jobTypes;
export const connections = (state) => state.settings.connections;
export const users = (state) => state.settings.users;
export const invites = (state) => state.settings.invites;
export const bundles = (state) => state.settings.bundles;
export const companyConnections = (state) => state.settings.companyConnections;
export const companyConnectionConfig = (state) => state.settings.companyConnectionConfig;
export const prompts = (state) => state.settings.prompts;
export const promptEvalsMetadata = (state) => state.settings.promptEvalsMetadata;
export const promptEvals = (state) => state.settings.promptEvals;
export const userTracking = (state) => state.settings.userTracking;
export const productFeatures = (state) => state.settings.productFeatures;
export const linearUsers = (state) => state.settings.linearUsers;
export const adminUsers = (state) => state.settings.adminUsers;
export const publicSlackChannelUsers = (state) => state.settings.publicSlackChannelUsers;
export const subscription = createSelector(
  'subscription',
  [(state) => state.settings?.subscription, (state) => state.settings?.productFeatures, (state) => state.settings?.featureOptions], //
  (subscription, productFeatures, featureOptions) => {
    if (!subscription || !productFeatures || !featureOptions) {
      return subscription;
    }

    const overrideFeatures = subscription?.overrideFeatures;

    const features = productFeatures
      ?.filter((feature) => feature.stripeProductId === subscription.stripeSubscriptionProductId)
      ?.map((feature) => {
        const filteredOptions = featureOptions
          ?.filter((option) => option.featureId === feature.featureId)
          ?.filter((option) => !option.stripeProductId || option.stripeProductId === subscription.stripeSubscriptionProductId)
          ?.sort((a, b) => +a.option - +b.option);

        const options = filteredOptions?.filter((option) => {
          const value = option?.option;
          const withSameValue = filteredOptions?.filter((el) => el?.option === value);

          if (withSameValue?.length > 1) {
            return option?.stripeProductId === subscription.stripeSubscriptionProductId;
          }
          return true;
        });

        const overrideFeature = overrideFeatures?.find((feat) => feat.featureId === feature.featureId);

        const value = options?.find((option) => option.id === feature.featureOptionId);
        const overrideValue = options?.find((option) => option.id === feature?.featureValueOverride);
        const companyOverrideValue = options?.find((option) => option.id === overrideFeature?.featureOptionId);

        const overrideObject = companyOverrideValue || overrideValue || {};
        const defaultValues = { defaultOptionId: feature?.featureOptionId, defaultOption: value?.option };

        return { ...value, ...overrideObject, ...defaultValues, options };
      });

    return {
      ...subscription,
      features: features || []
    };
  }
);
export const subscriptionFeatures = createSelector('subscriptionFeatures', [subscription], (subscription) => subscription?.features);
export const subscriptionFeature = createSelector(
  'subscriptionFeature',
  [subscriptionFeatures, (_, featureId) => featureId], //
  (features, featureId) => features?.find((feature) => feature?.featureId === featureId)
);
export const subscriptionPlan = createSelector('subscriptionPlan', [subscription], (subscription) => subscription?.subscription?.plan);
export const subscriptionName = createSelector('subscriptionName', [subscription, productFeatures], (subscription, productFeatures) => {
  if (subscription?.isLegacy) {
    return 'Enterprise Plan';
  }

  if (!subscription?.stripeSubscriptionProductId) {
    return 'Basic Plan';
  }

  const feature = productFeatures?.find((n) => n?.stripeProductId === subscription?.stripeSubscriptionProductId);
  return feature?.name;
});

export const roles = (state) => state.settings.roles;
export const settingsPermissions = (state) => state.settings.permissions;
export const rolesPermissions = (state) => state.settings.rolesPermissions;
export const rolesWithPermissions = createSelector('rolesWithPermissions', [roles, settingsPermissions, rolesPermissions], (roles, permissions, rolesPermissions) => {
  return roles.map((role) => {
    const rolePermissions = rolesPermissions.filter((rolePermission) => rolePermission.roleId === role.id);
    const result = rolePermissions.map((rolePermission) => permissions.find((permission) => permission.id === rolePermission.permissionId));
    return { ...role, permissions: result };
  });
});

export const promptEvalsByName = createSelector(
  'promptEvalsByName',
  [promptEvalsMetadata, (_, promptName) => promptName], //
  (promptEvalsMetadata, promptName) => {
    return promptEvalsMetadata?.filter((promptEval) => promptEval.promptName === promptName);
  }
);

export const promptEvalById = createSelector(
  'promptEvalById',
  [promptEvals, (_, id) => id], //
  (promptEvals, id) => {
    return promptEvals?.find((promptEval) => promptEval.id === id);
  }
);

export const environments = (state) => state.settings.environments;
export const environmentById = createSelector(
  'environmentById',
  [environments, (_, id) => id], //
  (environments, id) => environments?.find((environment) => environment.id === id)
);

export const mindCloudEmbeddedEndUserId = (state) => state.settings.mindCloudEmbeddedEndUserId;
export const integrators = (state) => state.settings.integrators;
export const integratorsProjects = (state) => state.settings.integratorsProjects;
export const integratorsIssues = (state) => state.settings.integratorsIssues;
export const integrationGraphs = (state) => state.settings.integrationGraphs;
export const ftpUsers = (state) => state.settings.ftpUsers;

export const projectIssues = (state) => state.settings.projectIssues;
export const projectIssuesByProjectId = createSelector(
  'projectIssuesByProjectId',
  [projectIssues, (_, projectId) => projectId], //
  (issues, projectId) => issues?.filter((issue) => issue.projectId === projectId) || []
);
export const projectIssueById = createSelector(
  'projectIssueById',
  [projectIssues, (_, issueId) => issueId], //
  (issues, issueId) => issues?.find((issue) => issue.id === issueId)
);

export const platformSecrets = (state) => state.settings.platformSecrets;
export const platformSecretsByAppId = createSelector(
  'platformSecretsByAppId',
  [platformSecrets, (_, appId) => appId], //
  (items, appId) => (items || []).filter((n) => n?.appId === appId)
);
export const platformSecretById = createSelector(
  'platformSecretById',
  [platformSecrets, (_, id) => id], //
  (items, id) => (items || []).find((n) => n?.id === id) || null
);
